<template>
  <Card :link-path="cardLinkPath"
        :link-text="cardLinkText"
        :title="tableMembers.length > 1 ? t('global.members') : t('global.member')">
    <DataTable
        :headers="memberHeader"
        :is-loading="isLoading"
        :items="tableMembers"
        @delete="deleteMember"
        @resend-user-invite="resendUserInvite"
    />
  </Card>
</template>
<script lang="ts" setup>
import DataTable from "@/components/layout/DataTable.vue";
import Card from "@/components/layout/Card.vue";
import {useI18n} from "vue3-i18n";
import {Member} from "@/types/entities/member";
import {computed, ref} from "vue";
import {useMemberStore} from "@/stores/memberStore";
import {Guid, Role} from "@/types";
import {useMemberService, useUserService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";
import {getAuthenticatedMemberOrganizationRoute} from "@/router/helpers";

// eslint-disable-next-line
const props = defineProps<{
  members: Member[]
  organizationId: string
  cardLinkPath: any
  isLoading: boolean,
  hideCardLink?: boolean
}>()

const {t} = useI18n()
const memberStore = useMemberStore()
const userService = useUserService()
const memberService = useMemberService()

const members = ref<Member[]>(props.members);

const canAddEditOrDeleteMember = memberStore.hasOneOfTheseRoles([Role.Admin, Role.RegularSyndicateAdmin, Role.RegularSyndicateManager])
const cardLinkPath = canAddEditOrDeleteMember && !props.hideCardLink ? props.cardLinkPath : undefined
const cardLinkText = canAddEditOrDeleteMember && !props.hideCardLink ? t('organization.addMember') : undefined

const tableMembers = computed(() => {
  return members.value.map((member: any) => {
    let item = {...member}
    item.roles = member.roles ? member.roles.map((x: any) => t(`user.roles.${x}`)).join(', ') : undefined
    if (canAddEditOrDeleteMember) {
      item.actions = {
        resendUserInvite: true,
        update: {
          name: memberStore.hasRole(Role.Admin)
              ? 'admin.children.organizations.editMember'
              : `${getAuthenticatedMemberOrganizationRoute()}.children.editMember`,
          params: {organizationId: member.organizationId, memberId: member.id}
        },
        delete: member.email != memberStore.getMember.email
      }
    }
    return item;
  })
})

async function resendUserInvite(userId: string) {
  let response = await userService.resendInvite(userId)

  if (response.succeeded) {
    notifySuccess(t('user.inviteSuccessfullySent'))
  } else {
    let errorMessages = response.getErrorMessages('user', 'user.sendingInviteFailed')
    notifyError(errorMessages[0])
  }
}

async function deleteMember(member: any) {
  if (member?.id == null || !Guid.isValid(member.id ?? ""))
    return

  let confirmDelete = confirm(t("validation.member.delete.confirmation"));
  if (!confirmDelete)
    return;

  let response = await memberService.deleteMember(member.id, props.organizationId)
  if (response.succeeded) {
    members.value = members.value.filter(x => x.id !== member.id)
    notifySuccess(t('validation.member.delete.success'))
  } else {
    let errorMessages = response.getErrorMessages('validation.member.delete',
        'validation.member.delete.errorOccurred')
    notifyError(errorMessages[0])
  }
}

let memberHeader = [
  {
    text: t("global.firstName"),
    value: "firstName",
    sortable: true,
    width: 125,
  },
  {
    text: t("global.lastName"),
    value: "lastName",
    sortable: true,
    width: 125,
  },
  {
    text: t("global.email"),
    value: "email",
    sortable: true,
    width: 100,
  },
  {
    text: t("global.roles"),
    value: "roles",
    sortable: true,
    width: 200,
  }
]

if (canAddEditOrDeleteMember) {
  memberHeader.push({
    text: t("global.table.actions"),
    value: "actions",
    sortable: false,
    width: 125
  })
}
</script>